//
// Genesys Metatour JS
// --------------------------------------------------
// This file runs the front-end show. The most important
// function in here is goToStop(), which is used for
// most navigation-related functionality.
//
// Also, a system relying on the URL hash has been
// employed to get the built-in browser benefits
// of back/forward capability and to have a theoretical
// source of truth (the URL, though from a code standpoint
// I'm not sure it's really coded as the source of truth).
// It also allows the client to link to the tour at
// a specific Stop and have that Stop be the one that loads.
// --------------------------------------------------


// Imports
// ---------------------------------------
import { createPopper } from '@popperjs/core';
import { debounce, getPreviousSiblings } from './helpers.js';
import handlePopoverCopyUrlButton from './copyURLButton.js';
import { checkUniversalBarForCrumbsOverflow } from './crumbs-overflow-system.js';


// Variables
// ---------------------------------------
const root = document.documentElement;
const mobileMaxWidthBp = "767px";

const windowParent = window.parent;
const tourFrame = document.querySelector("#tour-frame");
const tourElement = document.querySelector("#gctour");
const tourStopsWrapper = document.querySelector('#gctour-stops-wrapper');
const tourStops = document.querySelectorAll('.gctour-stop-wrapper');
const firstStop = document.querySelector('#gctour\\[0\\]-stop-1');
const welcomeStop = document.querySelector('#welcome');
const welcomePopover = document.querySelector('#popover-welcome');
const endStop = document.querySelector('#end');
const endPopover = document.querySelector('#popover-end');
const hotspotsWrapper = document.querySelector('#gctour-hotspots-wrapper');
const hotspots = document.querySelectorAll('.gctour-hotspot');
const popovers = document.querySelectorAll('.popover');

const childTourCrumbItems = document.querySelectorAll('.child-tours-crumb-item');
const tourUiBackdrop = document.querySelector('#gctour-ui-backdrop');

const hotspotsLists = document.querySelectorAll('.gctour-hotspots-list');
const numberOfChildTours = Number(tourElement.dataset.numberOfTours);

let initialStopID = tourElement.getAttribute("data-initial-stop");
let initialStopHash = !initialStopID || !initialStopID.length ? "#welcome" : "#" + initialStopID;
let popperInstance = null;

const popperPositioning = {
  'top-left': {
    'placement': 'left-end',
    'offset': [-18, -14]
  },
  'top-right': {
    'placement': 'right-end',
    'offset': [-18, -14]
  },
  'bottom-right': {
    'placement': 'right-start',
    'offset': [18, -14]
  },
  'bottom-left': {
    'placement': 'left-start',
    'offset': [18, -14]
  }
};

// Functions
// ---------------------------------------

/**
 * Creates a Popper instance. Adheres to Popper.js' method/documentation
 * @param {HTMLElement} hotspot - The hotspot element to hook the popper to
 * @param {HTMLElement} popover - The popover element to show
 * @param {String} positioning - Chosen preset to attept to position to. Options are 'top-left', 'top-right', 'bottom-right', 'bottom-left'.
 */
function initPopperInstance(hotspot, popover, positioning) {
  popperInstance = createPopper(hotspot, popover, {
    placement: popperPositioning[positioning].placement,
    modifiers: [
      {
        name: 'offset',
        options: { offset: popperPositioning[positioning].offset, }
      },
      {
        name: 'flip',
        options: { boundary: tourStopsWrapper, },
      },
    ],
  });
}

/**
 * Destroys the Popper instance. Adheres to Popper.js' method/documentation
 */
function destroyPopperInstance() {
  if (popperInstance) {
    popperInstance.destroy();
    popperInstance = null;
  }
}

/**
 * Shows a Popper instance. Also adds the data-show data attribute.
 * @param {HTMLElement} hotspot - The hotspot element to hook the popper to
 * @param {HTMLElement} popover - The popover element to show
 */
function showPopper(hotspot, popover) {
  popover.setAttribute('data-show', '');
  let positioning = popover.dataset.positioning;
  if (!positioning) {
    positioning = 'bottom-right';
  }
  initPopperInstance(hotspot, popover, positioning);
}

/**
 * Hides a Popper instance. Also removes the data-show data attribute.
 * @param {HTMLElement} popover - The popover element to hide
 */
function hidePopper(popover) {
  popover.removeAttribute('data-show');
  destroyPopperInstance();
}

/**
 * Returns the popover element linked to the hotspot provided
 * @param {HTMLElement} hotspot - The hotspot element to search out its linked popover
 * @return {HTMLElement} The linked popover element
 */
function getHotspotLinkedPopover(hotspot) {
  return document.querySelector(escapeSelectorString(`#${hotspot.dataset.linkedPopover}`));
}

/**
 * Returns a stop's mobile focal point
 * @param {HTMLElement} stop - The stop element to search out its set mobile focal point (set as a CSS Custom Property)
 * @return {string} The mobile vertical focal point, represented as a string, with the number and percentage sign (eg. "34%")
 */
function getStopMobileFocalPoint(stop) {
  return getComputedStyle(stop).getPropertyValue('--mobile-vert-focal-point');
}

/**
 * Show only the active tour's hotspots; hide the rest
 * @param {String | Number} activeTourNumber - The active tour number. Tours numbering is zero based.
 */
function showOnlyActiveTourHotspots(activeTourNumber) {
  hotspotsLists.forEach((hsList) => {
    hsList.hidden = (hsList.dataset.fromTour === activeTourNumber) ? false : true;
  });
}

/**
 * Removes all of the relevant state-related CSS classes and custom properties from ALL stops
 */
function removeAllStopActiveStates() {
  welcomeStop.classList.remove('stop-is-active');

  endStop.classList.remove('stop-is-active');

  tourStops.forEach(stop => stop.classList.remove('stop-is-active'));

  childTourCrumbItems.forEach((childTourCrumbItem) => {
    // console.log('childTourCrumbItem: ', childTourCrumbItem);
    childTourCrumbItem.classList.remove('is-active');
    const stops = childTourCrumbItem.querySelectorAll('.tour-stop-list-item');
    stops.forEach((stop) => {
      stop.classList.remove('is-active');
    })
  });

  hotspotsWrapper.classList.remove('manual-include-mode');

  hotspots.forEach(hs => {
    // console.log(hs);
    hs.parentNode.classList.remove('is-a-previous-stop');
    hs.classList.remove('hidden', 'included');
    hs.parentNode.style.removeProperty("--override-pos-x");
    hs.parentNode.style.removeProperty("--override-pos-y");
    hs.closest('.gctour-hotspot-item').classList.remove('is-active');
  });

  popovers.forEach(popover => { hidePopper(popover); popover.classList.remove('is-active'); });
}

/**
 * Escapes selector stings so they can be used without error in .querySelector type functions
 * @param {string} selector - 
 */
function escapeSelectorString(selector) {
  if (selector.includes('[') || selector.includes(']')) {
    return selector.replace('[', '\\[').replace(']', '\\]');
  }
  else {
    return selector;
  }
}

/**
 * Goes to a stop based on the provided selector
 * @param {string} selector - The selector to use for stop navigation (eg. "#gctour\\[0\\]-stop-1")
 */
function goToStop(selector) {
  // console.log('received selector: ', selector);

  // check to make sure that the selector is valid
  if (!selector) {
    console.info('goToStop\'s selector is falsy. Returning false.');
    return false;
  }

  if (
    selector === '#welcome' ||
    selector === '#end'
  ) {
    goToFauxStop(selector);

    return true;
  }
  // check to make sure that the URL hash starts with #gctour[
  else if (!selector.startsWith('#gctour[')) {
    console.info('URL hash does not match the tour stop selector pattern. Going to the initial stop (#welcome in most cases).');
    //location.hash = '#welcome';
    location.hash = initialStopHash;
    return false;
  }

  let previousStop = getCurrentStop();

  // get the new tour stop from the selector
  const newStop = document.querySelector(escapeSelectorString(selector));
  // make sure the selector provided found an element
  if (!newStop) { console.info('Invalid Stop in URL. Returning false'); return false; }
  const newStopNum = newStop.dataset.stop;
  const newStopTourNum = newStop.dataset.withinTour;

  // get the new tour stop's related UI elements from the selector
  let newStopHotspot = document.querySelector(`.gctour-hotspot[href="${selector}"]`);
  const newStopCrumbItem = Array.from(childTourCrumbItems).find(item => item.dataset.tourNumber === newStopTourNum);
  let newPopover = getHotspotLinkedPopover(newStopHotspot);
  // let newPopoverArrow = newPopover.querySelector("[data-popper-arrow]");

  // get the hotspots to include (or exclude) on this new stop
  let newHsIncludes = newStop.dataset.hsVisibilityInclude;
  newHsIncludes = (newHsIncludes) ? newHsIncludes.split(',') : null;
  // console.log("Include list: ", (newHsIncludes) ? newHsIncludes : "null");
  let newHsExcludes = newStop.dataset.hsVisibilityExclude;
  newHsExcludes = (newHsExcludes) ? newHsExcludes.split(',') : null;
  // console.log("Exclude list: ", (newHsExcludes) ? newHsExcludes : "null");

  // get the hotspots to reposition on this new stop
  let newHsRepositionList = newStop.dataset.hsRepositionList;
  // console.log("newHsRepositionList: ", newHsRepositionList);
  newHsRepositionList = (newHsRepositionList) ? newHsRepositionList.split(';') : null;
  // console.log("Reposition array: ", (newHsRepositionList) ? newHsRepositionList : "null");

  // set the background position and transform origin properties (via a custom property, so only mobile is targeted) on all stops, allowing CSS transitions to handle tweening from one stop to another
  // let previousStopFocalPt = (previousStop) ? getStopMobileFocalPoint(previousStop) : null;
  let newStopFocalPt = getStopMobileFocalPoint(newStop);
  tourStops.forEach(stop => {
    stop.style.backgroundPositionY = newStopFocalPt;
    stop.style.transformOrigin = `50% ${newStopFocalPt}`;
    if (previousStop && previousStop.id !== 'welcome') { stop.style.removeProperty('--mobileTransitionDelay'); }
  });

  // hide all other tour stops
  removeAllStopActiveStates();

  // show and activate appropriate stop and related UI elements
  newStop.classList.add('stop-is-active');
  if (newStopCrumbItem) {
    newStopCrumbItem.classList.add('is-active');
    newStopCrumbItem.querySelector(`.tour-stop-list-item[data-stop-number="${newStopNum}"]`).classList.add('is-active');
  }
  (newStopHotspot) ? newStopHotspot.parentNode.classList.add('is-active') : "";
  // INcludes
  if (newHsIncludes) {
    hotspotsWrapper.classList.add('manual-include-mode');
    newHsIncludes.forEach(id => {
      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${id}"]`)
      // console.log("stop.id", stop.id);

      let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
      // console.log(hs);
      hs.classList.add('included');
    });
  }
  // EXcludes
  if (newHsExcludes) {
    newHsExcludes.forEach(id => {
      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${id}"]`)
      // console.log("stop.id", stop.id);

      // hide the chosen stop's hotspots
      // prevent the user from excluding its own hotspot
      if ( !stop.classList.contains('stop-is-active') ) {
        let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
        // console.log(hs);
        hs.classList.add('hidden');
      }
    });
  }
  if (newHsRepositionList) {
    newHsRepositionList.forEach(hs => {
      let hsObj = JSON.parse(hs);
      // console.log('hotspot reposition object: ', hsObj);

      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${hsObj.stopId}"]`)
      // console.log(stop);

      // reposition the chosen hotspots
      // prevent the user from repositioning its own hotspot
      if ( !stop.classList.contains('stop-is-active') ) {
        let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
        // console.log(hs);
        hs.parentNode.style.setProperty("--override-pos-x", hsObj.newPosX);
        hs.parentNode.style.setProperty("--override-pos-y", hsObj.newPosY);
      }
    });
  }
  showPopper(newStopHotspot, newPopover);
  newPopover.classList.add('is-active');

  // tell elements what tour and stop is open
  tourElement.style.setProperty("--active-tour", newStopTourNum);
  tourElement.dataset.activeTour = newStopTourNum;
  showOnlyActiveTourHotspots(newStopTourNum);
  tourStopsWrapper.style.setProperty("--active-stop", newStopNum);

  // visually idicate the hotspots of previous spots
  let newStopPreviousStops = getPreviousSiblings(newStopHotspot.parentNode, '.gctour-hotspot-item');
  // console.log(newStopHotspot);
  if (newStopPreviousStops) {
    newStopPreviousStops.forEach((el) => {
      el.classList.add('is-a-previous-stop');
    });
  }

  // backdrop
  updateBackdropCoordinates(newStop);
  tourUiBackdrop.classList.add('is-active');

  checkUniversalBarForCrumbsOverflow();
}

/**
 * Goes to a "Faux Stop" (eg. Welcome, End) based on the provided selector
 * @param {string} selector - The selector to use for stop navigation (eg. "#welcome")
 */
function goToFauxStop(selector) {
  // console.log(`welcome or end selector found -- going to the ${selector} panel`);
  removeAllStopActiveStates();
  let newStop = document.querySelector(selector);

  if (selector === '#welcome') {
    welcomeStop.classList.add('stop-is-active');
    updateBackdropCoordinates(newStop);
    tourUiBackdrop.classList.add('is-active');
    hotspots.forEach(hs => { hs.classList.add('hidden'); });
    showPopper(welcomeStop, welcomePopover);

    tourStops.forEach(stop => {
      stop.style.backgroundPositionY = `50%`;
      stop.style.transformOrigin = `50% 50%`;
    });
    firstStop.style.setProperty("--mobileTransitionDelay", "1s");
  }
  else if (selector === '#end') {
    endStop.classList.add('stop-is-active');
    updateBackdropCoordinates(newStop);
    tourUiBackdrop.classList.add('is-active');
    hotspots.forEach((el) => { el.parentNode.classList.add('is-a-previous-stop'); });
    showPopper(endStop, endPopover);
  }

  checkUniversalBarForCrumbsOverflow();
}

/**
 * Gets the currently active Stop
 * @return {HTMLElement} The currently active Stop
 */
function getCurrentStop() {
  return document.querySelector('.stop-is-active');
}

/**
 * Gets the previous Stop, referring to the currently active Stop
 * @return {String} The previous Stop's selector
 */
function getPrevStopSelector() {
  const currentStop = getCurrentStop();
  const currentStopNum = parseInt(currentStop.dataset.stop, 10);
  const currentTourNum = parseInt(currentStop.dataset.withinTour, 10);

  if (
    (tourElement.dataset.initialStop !== 'welcome') &&
    currentStop === firstStop
  ) {
    // console.log('No welcome, and the current stop is the first stop! Returning the first stop selector.');
    return `#gctour[0]-stop-1`;
  }
  // otherwise, if on the end stop, go back to the previous stop
  else if (currentStop.id === 'end') {
    const priorStop = currentStop.previousElementSibling;
    const priorTourNum = priorStop.dataset.withinTour;
    const priorTourPrevStopNum = priorStop.dataset.stop;
    return `#gctour[${priorTourNum}]-stop-${priorTourPrevStopNum}`;
  }
  // otherwise, current stop is currently welcome, or if the current stop is the first stop of the first tour, go to welcome
  else if (
    (currentStop.id === 'welcome') ||
    (currentTourNum === 0) && (currentStopNum === 1)
  ) {
    return `#welcome`;
  }
  // otherwise, if the current stop is the first stop in a tour, go back to the previous tour's last stop (coincedentally we can shortcut this by just going back an item in the DOM)
  else if (currentStopNum === 1) {
    const priorTourNum = currentTourNum - 1;
    const priorTourPrevStopNum = currentStop.previousElementSibling.dataset.stop;
    return `#gctour[${priorTourNum}]-stop-${priorTourPrevStopNum}`;
  }
  // otherwise, just go "down" one stop within the current tour
  else {
    return `#gctour[${currentTourNum}]-stop-${currentStopNum - 1}`;
  }
}

/**
 * Gets the next Stop, referring to the currently active Stop
 * @return {String} The next Stop's selector
 */
function getNextStopSelector() {
  const currentStop = getCurrentStop();
  // console.log(currentStop);
  const currentStopNum = parseInt(currentStop.dataset.stop, 10);
  const currentTourNum = parseInt(currentStop.dataset.withinTour, 10);

  // if on welcome, go to the first stop
  if (currentStop.id === 'welcome') {
    return `#gctour[0]-stop-1`;
  }
  // otherwise, on the end stop, or if the next stop is the end stop, send to `#end`
  else if (
    (currentStop.id === 'end') ||
    (currentStop.nextElementSibling.id === 'end')
  ) {
    return `#end`;
  }
  // otherwise, if the current stop's next sibling is a "first stop", proceed to it
  else if (currentStop.nextElementSibling.dataset.stop === '1') {
    const nextTourNum = currentTourNum + 1;
    return `#gctour[${nextTourNum}]-stop-1`;
  }
  // otherwise, just go "up" one stop within the current tour
  else {
    return `#gctour[${currentTourNum}]-stop-${currentStopNum + 1}`;
  }
}

/**
 * Handler for hash change Stop navigation
 */
function handleHashChangeStopNavigation() {
  // console.log('handleHashChangeStopNavigation run. hash: ', location.hash);
  goToStop(location.hash);
}

/**
 * Updates the backdrop's relevant coordinate CSS Custom Properties
 * @param {HTMLElement} stop - The stop element to refer to
 */
function updateBackdropCoordinates(stop) {
  // since the admin's interactive active area selection tool can reverse the start/stop positions of both x & y coordinates, we need to find which point is lower 
  let clipXStart = Number(stop.dataset.backdropClipXStart.replace('%', ''));
  let clipXEnd = Number(stop.dataset.backdropClipXEnd.replace('%', ''));
  let clipYStart = Number(stop.dataset.backdropClipYStart.replace('%', ''));
  let clipYEnd = Number(stop.dataset.backdropClipYEnd.replace('%', ''));

  let clipXLower = (clipXStart < clipXEnd) ? clipXStart + '%' : clipXEnd + '%';
  let clipXHigher = (clipXStart > clipXEnd) ? clipXStart + '%' : clipXEnd + '%';
  let clipYLower = (clipYStart < clipYEnd) ? clipYStart + '%' : clipYEnd + '%';
  let clipYHigher = (clipYStart > clipYEnd) ? clipYStart + '%' : clipYEnd + '%';

  root.style.setProperty("--backdrop-clip-x-start", clipXLower);
  root.style.setProperty("--backdrop-clip-x-end", clipXHigher);
  root.style.setProperty("--backdrop-clip-y-start", clipYLower);
  root.style.setProperty("--backdrop-clip-y-end", clipYHigher);

  let activeAreaWidth = Math.abs(clipXEnd - clipXStart);
  // console.log('active area width: ', activeAreaWidth);
  root.style.setProperty("--backdrop-active-area-width", activeAreaWidth);
  let activeAreaHeight = Math.abs(clipYEnd - clipYStart);
  // console.log('active area height: ', activeAreaHeight);
  root.style.setProperty("--backdrop-active-area-height", activeAreaHeight);
}

/**
 * Checks if the window is at or below the defined mobile width
 * @return {boolean} True if at mobile width
 */
function checkIfIsMobileWidth() {
  return window.matchMedia(`(max-width: ${mobileMaxWidthBp})`).matches;
}

/**
 * Handle Embed sizing
 */
function handleEmbedSizing() {
  let height = null;
  // console.log("Embed Sizing", height, window.frameElement);
  if (root.classList.contains('has-tour-frame')) {
    height = tourFrame.offsetHeight;
  }
  else {
    height = tourElement.offsetHeight;
  }

  window.frameElement.style.height = height + "px";
  return true;
}


// Event Listeners & inits
// ---------------------------------------
window.addEventListener('keydown', (event) => {
  // Escape Key closes the poppers
  // --------------------------
  if ( (popperInstance) && (event.key === "Escape" || event.key === "Esc") ) {
    hidePopper(popperInstance.state.elements.popper);
  }

  // Use keyboard arrow keys for navigation
  // --------------------------
  else if ( event.key === "ArrowRight" ) {
    // console.log('right arrow key entered');
    let selector = getNextStopSelector();
    location.hash = selector;
    // goToStop(selector);
  }
  else if ( event.key === "ArrowLeft" ) {
    // console.log('Left arrow key entered');
    let selector = getPrevStopSelector();
    location.hash = selector;
    // goToStop(selector);
  }
});


// Window hash change
// --------------------------
window.onhashchange = handleHashChangeStopNavigation;

window.addEventListener('click', (evt) => {
  if ( evt.target.tagName.toLowerCase() === 'a' ) {
    let href = evt.target.getAttribute("href");

    // if a hash'd href
    if (href.substring(0, 1) === '#') {
      // prevents scrolling
      evt.preventDefault();
      // think of this as a "softer" way of adjusting the hash. It allows us to cancel the event default, which causes the browser to jump / scroll to the tour (bad if embedded in an iframe). Note that setting location.hash = href; manually here would still scroll the browser in Chrome.
      history.replaceState(null, '', href);
    }
  }

  // Popover dismiss buttons
  // --------------------------
  if ( evt.target.closest('.dismiss-button') ) {
    let popover = evt.target.closest('.popover');
    hidePopper(popover);
  }

  // Condition for when the popover has been closed/dismissed, but the user is still requesting that the current stop be shown (again). This is necessary because the app mostly runs on watching for changes to the location.hash, but in this case, the hash does not change
  // --------------------------
  else if (
    evt.target.closest('[href]') && 
    !evt.target.closest('[href]').classList.contains('dismiss-button') &&
    (evt.target.closest('[href]').getAttribute("href") === location.hash)
  ) {
    // console.log('link clicked, and its href is the same as the currently active stop')
    goToStop(location.hash);
  }

  // if the backdrop is clicked, close the popover & backdrop
  else if ( (evt.target === tourUiBackdrop) ) {
    if (popperInstance) {
      console.log("currentStop", getCurrentStop());
      hidePopper(popperInstance.state.elements.popper);
    }
    if(location.hash === "#welcome") {
      goToStop("#gctour\\[0\\]-stop-1");
    }
    tourUiBackdrop.classList.remove('is-active');
  }
});

// Window resize
// --------------------------
// window.addEventListener('resize', handleResize);


// Location Hash Inits
// --------------------------
if (
  // if the location hash matches a Faux Stop or the URL/hash pattern for the Stops
  location.hash === "#welcome" ||
  location.hash.match(/^#gctour\[\d\]-stop-/) ||
  location.hash === "#end"
  ) {
  goToStop(location.hash);
}
else if ( location.hash === "" ) {
  // go to the Welcome stop if no hash is defined
  // console.info('The URL hash is empty. Going to Welcome.')
  //location.hash = "#gctour[0]-stop-1";
  //location.hash = "#welcome";
  location.hash = initialStopHash;
  // console.log(initialStopHash);
}
else {
  // A non-matching hash is used in the URL
  console.info('The URL hash does not match any of the available options. Going to Welcome.')
  //location.hash = "#gctour[0]-stop-1";
  //location.hash = "#welcome";
  location.hash = initialStopHash;
}


// Inits if embedded in an iframe
// --------------------------
if ( window.frameElement ) {
  // since we're in an iframe, grab the parent document
  let parentDocument = windowParent.document;
  // grab all tour iframes on the page
  let tourIframes = parentDocument.querySelectorAll('.tour_frame-wrapper > iframe');

  // method for revealing / unhiding the iframe -- necessary to mitigate an issue where loading the iframe to a hash URL would scroll the browser (Chrome) down to the iframe on the page. There are next to no clearly documented fixes for this issue; the below is a modified take on a reported fix. The modification in this case was to add a setTimeout timer to the mix; timer values of 1ms and 12ms were tried, but both resulted in _occasional_ scrolls down to the iframe on hard refreshes. 120ms seems to be a sweetspot in initial testing (~70 hard refreshes @ different intervals) -- no scrolls down. Wacky, but it works... <3 KB
  function iframeRevealMethod(tourIframe) {
    setTimeout(() => {
      tourIframe.style.display = "block";
      // now that the iframe is showing, the container needs to be dynamically sized
      handleEmbedSizing();
    }, 120);
  }

  tourIframes.forEach((tourIframe) => {
    tourIframe.addEventListener('load', () => { iframeRevealMethod(tourIframe) });
  });

  windowParent.addEventListener('resize', function() {
    debounce(handleEmbedSizing());
  });
}
